import React, { useMemo, useState } from 'react';
import { ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { getTagTooltip } from '@/utils/drill-ui';

interface MultiSelectProps {
  placeholder?: string;
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  /** Optional map of value -> display label for showing friendly names */
  optionLabels?: Record<string, string>;
  /** Optional map of value -> tooltip; if omitted, derived for IR/ER abbreviations */
  optionTooltips?: Record<string, string>;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  placeholder = 'Select values',
  options,
  selectedValues,
  onChange,
  optionLabels,
  optionTooltips,
}) => {
  const [open, setOpen] = useState(false);

  const optionSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const toggleValue = (value: string, shouldSelect?: boolean) => {
    const next = new Set(optionSet);
    const willSelect = typeof shouldSelect === 'boolean' ? shouldSelect : !next.has(value);
    if (willSelect) {
      next.add(value);
    } else {
      next.delete(value);
    }
    onChange(Array.from(next));
  };

  const clearSelection = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange([]);
  };

  const formatOption = (v: string) => (optionLabels?.[v] ?? v);
  const getOptionTooltip = (v: string) => optionTooltips?.[v] ?? getTagTooltip(v);
  const displayLabel = optionSet.size === 0
    ? placeholder
    : optionSet.size <= 2
      ? Array.from(optionSet).join(', ')
      : `${Array.from(optionSet).slice(0, 2).join(', ')} +${optionSet.size - 2}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full min-w-[14rem] max-w-full h-12 justify-between"
          title={optionSet.size > 0 ? Array.from(optionSet).join(', ') : undefined}
        >
          <span className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
            {displayLabel}
            {optionSet.size > 0 && (
              <Badge variant="secondary" className="ml-1">
                {optionSet.size}
              </Badge>
            )}
          </span>
          <div className="flex items-center gap-2">
            {optionSet.size > 0 && (
              <X
                className="h-4 w-4 opacity-60 hover:opacity-100"
                onClick={clearSelection}
              />
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[32rem] max-w-[95vw] p-2" align="start">
        <div className="max-h-60 overflow-auto space-y-2">
          {options.length === 0 ? (
            <p className="text-sm text-muted-foreground px-1">No options available.</p>
          ) : (
            options.map(option => {
              const isSelected = optionSet.has(option);
              return (
                <label
                  key={option}
                  className={cn(
                    'flex items-start gap-2 rounded-md px-3 py-2 text-sm transition cursor-pointer',
                    isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                  )}
                  title={getOptionTooltip(option)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) =>
                      toggleValue(option, Boolean(checked))
                    }
                    className="mt-0.5 shrink-0"
                  />
                  <span className="flex-1 min-w-0 break-words leading-tight">{option}</span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelect;

